import { useEffect, useState } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import i18next from "i18next";
import { AndCondition, ComparisonCondition, ComparisonOperator, ComparisonType, Condition, ConjunctionCondition, MeasurementType, MeasurementTypeI18N, MeasurementUnit, OrCondition } from "@the-real-insight/tri-model";

import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { NO_UPLINK_COMPARISON_TYPE, NO_UPLINK_FIELD, NO_UPLINK_OPERATOR, isNoUplinkField, parseDaysValue } from "@/settings/noUplink";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface ConditionProperties {
    condition: Condition;
    setCondition: (condition: Condition) => void;
    /** Translation function of the owning panel; without it the German defaults are shown. */
    t?: (key: string, values?: Record<string, string>) => string;
    /** Inside an AND/OR group: the "no uplink" pseudo-field is not offered there. */
    nested?: boolean;
}

const TEXT_NAMESPACE = 'settingsPanel.settingsPanel.alertManagementPanel.';

/** German defaults, used when no translation function is passed (e.g. in isolated tests). */
const DEFAULT_TEXT: Record<string, string> = {
    noUplinkField: 'Tage ohne Uplink',
    noUplinkUnit: 'Tage',
    noUplinkHelp: 'Meldet aktivierte Geräte (nicht in Wartung, stillgelegt oder im Schlafmodus) nach N Tagen ohne Daten. Läuft ab dem Speichern, Prüfung stündlich.',
    noUplinkAlone: 'Diese Bedingung sollte allein stehen.',
    noUplinkInvalid: 'Bitte eine ganze Zahl ab 1 eingeben.',
    noUplinkValuePlaceholder: 'Anzahl Tage',
    operatorEqual: 'ist gleich =',
    operatorLess: 'ist kleiner als <',
    operatorLessEqual: 'ist kleiner als oder gleich ≤',
    operatorGreater: 'ist größer als >',
    operatorGreaterEqual: 'ist größer als oder gleich ≥',
    valuePlaceholder: 'Wert eingeben',
    conditionAnd: 'UND',
    conditionOr: 'ODER',
    addCondition: 'Bedingung ergänzen',
    compareWithMeasurement: 'Mit Messgröße vergleichen',
    compareWithValue: 'Mit festem Wert vergleichen',
};

/** The comparison operators, in display order; the label comes from `operator<Name>` in the locale. */
const OPERATORS: ReadonlyArray<{ key: ComparisonOperator; textKey: string }> = [
    { key: ComparisonOperator.equal, textKey: 'operatorEqual' },
    { key: ComparisonOperator.less, textKey: 'operatorLess' },
    { key: ComparisonOperator.lessEqual, textKey: 'operatorLessEqual' },
    { key: ComparisonOperator.greater, textKey: 'operatorGreater' },
    { key: ComparisonOperator.greaterEqual, textKey: 'operatorGreaterEqual' },
];


/**
 * A complete decimal number as typed by a user: optional sign, digits, and a dot or comma as
 * decimal separator. Intermediate states such as "-" or "." do not match.
 */
const DECIMAL_PATTERN = /^[+-]?(\d+([.,]\d*)?|[.,]\d+)$/;

/**
 * Parses the text of the value field. Empty or incomplete text yields `undefined`, so the model
 * never receives `NaN` (and an empty field never becomes `0`).
 */
export const parseComparisonValue = (text: string): number | undefined => {
    const trimmed = text.trim();

    if (!DECIMAL_PATTERN.test(trimmed)) {
        return undefined;
    }

    const value = Number(trimmed.replace(',', '.'));

    return Number.isFinite(value) ? value : undefined;
};

const valueToText = (value: unknown): string =>
    value === undefined || value === null || (typeof value === 'number' && !Number.isFinite(value))
        ? ''
        : String(value);

/** Whether the typed text already represents the model value (then the text is kept as typed). */
const textMatchesValue = (
    text: string,
    value: unknown,
    parse: (text: string) => number | undefined = parseComparisonValue,
): boolean => {
    const parsed = parse(text);

    if (parsed === undefined) {
        return value === undefined || value === null || valueToText(value) === '';
    }

    return parsed === Number(value);
};

/**
 * Value field of a comparison. Keeps the raw text locally so that intermediate input like "-",
 * "2." or "2," stays visible while typing, and only hands finite numbers (or `undefined` for
 * empty/incomplete input) to the model.
 */
function ComparisonValueInput(properties: {
    value: unknown;
    onChange: (value: number | undefined) => void;
    /** Whole numbers >= 1 only (days), no sign and no decimals. */
    integerOnly?: boolean;
    placeholder?: string;
    invalidText?: string;
}) {
    const { value, onChange, integerOnly } = properties;
    const parse = integerOnly ? parseDaysValue : parseComparisonValue;
    const [text, setText] = useState(() => valueToText(value));

    // Adopt external model changes (e.g. another rule loaded), but keep the typed text when it
    // already stands for the current value.
    useEffect(() => {
        setText((current) => (textMatchesValue(current, value, parse) ? current : valueToText(value)));
    }, [value, parse]);

    const invalid = !!integerOnly && text.trim() !== '' && parse(text) === undefined;

    return <div className="flex min-w-0 flex-col gap-1">
        <Input
            className="w-[200px] max-w-full"
            placeholder={properties.placeholder ?? 'Wert eingeben'}
            inputMode={integerOnly ? 'numeric' : 'decimal'}
            aria-invalid={invalid || undefined}
            value={text}
            onChange={(event) => {
                const nextText = event.target.value;
                const nextValue = parse(nextText);

                setText(nextText);

                if (nextValue !== value) {
                    onChange(nextValue);
                }
            }} />
        {invalid && <span role="alert" className="text-xs text-destructive">{properties.invalidText}</span>}
    </div>;
}

export default function ConditionSection(properties: ConditionProperties) {
    const condition = properties.condition;

    // Deliberately no local state: the owning panel holds the condition tree and
    // is what gets saved. Keeping a private copy of the children here is what
    // used to strand nested edits in this component — they never reached the
    // product, so a UND/ODER block persisted with default (empty) children.
    const conditions: Condition[] = condition instanceof ConjunctionCondition
        ? (condition.conditions ?? [])
        : [];

    const replaceConditions = (newConditions: Condition[]) => {
        properties.setCondition(Condition.unmarshal({ ...condition, conditions: newConditions }));
    };

    const addCondition = (newCondition: Condition) => {
        replaceConditions([...conditions, newCondition]);
    };

    const removeCondition = (removed: Condition) => {
        replaceConditions(conditions.filter((entry: Condition) => entry !== removed));
    };

    const text = (key: string): string => properties.t ? properties.t(`${TEXT_NAMESPACE}${key}`) : DEFAULT_TEXT[key];

    // tri-model carries the measurement names in German and English; any other language falls back to German.
    const measurementLanguage = i18next.resolvedLanguage === 'en' ? 'en' : 'de';
    const operator = OPERATORS.map((entry) => ({ key: entry.key, text: text(entry.textKey) }));

    const measurementOptions = Object.keys(MeasurementType).map((key: any) => ({
        key,
        // @ts-ignore
        text: MeasurementTypeI18N[measurementLanguage][key] as string,
    })).sort((x, y) => x.text.localeCompare(y.text, measurementLanguage));

    // The pseudo-field is not part of tri-model's MeasurementType; it is merged in as an extra
    // option. Not offered inside AND/OR groups (it must stand alone), but still shown there when
    // an existing rule already carries it.
    const noUplinkSelected = condition instanceof ComparisonCondition && isNoUplinkField(condition.field);
    const fieldOptions = [
        ...(properties.nested && !noUplinkSelected ? [] : [{ key: NO_UPLINK_FIELD, text: text('noUplinkField') }]),
        ...measurementOptions,
    ];

    return <div className="min-w-0">
        {(() => {
            if (condition instanceof ComparisonCondition) {
                return <div className='flex min-w-0 flex-col gap-1'>
                  <div className='flex min-w-0 flex-wrap items-center gap-2'>
                    <Select
                        value={condition.field ?? undefined}
                        onValueChange={(value: string) => {
                            const newCondition: any = isNoUplinkField(value)
                                // Lock operator and type; the days value must be a whole number >= 1.
                                ? {
                                    ...condition,
                                    field: value,
                                    operator: NO_UPLINK_OPERATOR,
                                    comparisonType: NO_UPLINK_COMPARISON_TYPE,
                                    comparisonField: undefined,
                                    comparisonValue: parseDaysValue(String(condition.comparisonValue ?? '')),
                                }
                                : isNoUplinkField(condition.field)
                                    ? { ...condition, field: value, comparisonValue: undefined }
                                    : { ...condition, field: value };

                            properties.setCondition(Condition.unmarshal(newCondition));
                        }}
                    >
                        <SelectTrigger className="w-[230px] max-w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                            {fieldOptions.map((option) => (
                                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                                    {option.text}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        disabled={noUplinkSelected}
                        value={(noUplinkSelected ? NO_UPLINK_OPERATOR : condition.operator) ?? undefined}
                        onValueChange={(value: string) => {
                            const newCondition = { ...condition, operator: value };

                            properties.setCondition(Condition.unmarshal(newCondition));
                        }}
                    >
                        <SelectTrigger className="w-[140px] max-w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                            {operator.map((option) => (
                                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                                    {option.text}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {/* Die Einheit stand im Bestand als `suffix` am Feld; der Shim
                        verwarf sie. Sie steht jetzt sichtbar daneben. */}
                    {noUplinkSelected
                        ?
                        <div className="flex min-w-0 items-start gap-2">
                            <ComparisonValueInput
                                integerOnly
                                placeholder={text('noUplinkValuePlaceholder')}
                                invalidText={text('noUplinkInvalid')}
                                value={condition.comparisonValue}
                                onChange={(comparisonValue) => {
                                    const newCondition = { ...condition, comparisonValue };

                                    properties.setCondition(Condition.unmarshal(newCondition));
                                }} />
                            <span className="pt-2 text-sm text-muted-foreground">{text('noUplinkUnit')}</span>
                        </div>
                        : condition.comparisonType === ComparisonType.value
                        ?
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <ComparisonValueInput
                                placeholder={text('valuePlaceholder')}
                                value={condition.comparisonValue}
                                onChange={(comparisonValue) => {
                                    const newCondition = { ...condition, comparisonValue };

                                    properties.setCondition(Condition.unmarshal(newCondition));
                                }} />
                            <span className="text-sm text-muted-foreground">
                                {condition.field ? MeasurementUnit['de'][condition.field] : ''}
                            </span>
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto px-1 text-xs"
                                onClick={() => properties.setCondition(Condition.unmarshal({
                                    ...condition,
                                    comparisonType: ComparisonType.field,
                                    comparisonValue: undefined,
                                }))}
                            >
                                {text('compareWithMeasurement')}
                            </Button>
                        </div>
                        :
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <Select
                                value={condition.comparisonField ?? undefined}
                                onValueChange={(value: string) => properties.setCondition(Condition.unmarshal({
                                    ...condition,
                                    comparisonField: value,
                                }))}
                            >
                                <SelectTrigger className="w-[220px] max-w-full" aria-label={text('compareWithMeasurement')}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-w-[calc(100vw-2rem)]">
                                    {measurementOptions.map((option) => (
                                        <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                                            {option.text}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto px-1 text-xs"
                                onClick={() => properties.setCondition(Condition.unmarshal({
                                    ...condition,
                                    comparisonType: ComparisonType.value,
                                    comparisonField: undefined,
                                }))}
                            >
                                {text('compareWithValue')}
                            </Button>
                        </div>
                    }
                  </div>
                  {noUplinkSelected && <>
                    <p className="max-w-xl text-xs text-muted-foreground">{text('noUplinkHelp')}</p>
                    {properties.nested && <p role="status" className="text-xs text-amber-600">{text('noUplinkAlone')}</p>}
                  </>}
                </div>
            } else if (condition instanceof ConjunctionCondition) {
                return <div className="flex min-w-0 border-l">
                    <div className='flex flex-col items-center justify-center p-2'>
                        {condition instanceof AndCondition
                            ?
                            <span className='font-bold [writing-mode:vertical-lr] [text-orientation:mixed]'>{text('conditionAnd')}</span>
                            :
                            <span className='font-bold [writing-mode:vertical-lr] [text-orientation:mixed]'>{text('conditionOr')}</span>
                        }
                    </div>
                    <div className="min-w-0 flex-1">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button type="button" variant="ghost" size="sm" className="gap-2">
                                    {text('addCondition')}
                                    <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 opacity-60" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuItem onSelect={() => addCondition(new ComparisonCondition())}>
                                    Vergleich
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => addCondition(new AndCondition())}>
                                    UND-Verknüpfung
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => addCondition(new OrCondition())}>
                                    ODER-Verknüpfung
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {conditions.map((childCondition: Condition, index: number) => <div key={`condition${index}`} className='mb-2 flex min-w-0 flex-wrap items-center gap-4'>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Bedingung entfernen"
                                onClick={() => {
                                    removeCondition(childCondition);
                                }}>
                                <Trash2 aria-hidden="true" className="h-4 w-4" />
                            </Button>
                            <ConditionSection nested t={properties.t} condition={childCondition} setCondition={(updatedCondition: Condition) => {
                                const newConditions = [...conditions];

                                newConditions[index] = updatedCondition;

                                replaceConditions(newConditions);
                            }}></ConditionSection>
                        </div>)}</div>
                </div>
            }
        })()}
    </div>;
}
